import * as api from "@/api/stationLibrary.api"
import { addPlaylistTrack } from "@/api/stationAutoDj.api"

const PRUNE_DELAY = 5000

let uploadSequence = 0
let pruneTimer = null

const failureReason = (e) => {
  if (!e?.response) {
    return e?.isAxiosError ? "network" : "unknown"
  }

  const { status } = e.response

  if (status === 413) return "too_large"
  if (status === 415) return "unsupported"
  if (status === 400 || status === 422) return "rejected"
  if (status === 401 || status === 403) return "unauthorized"
  if (status >= 500) return "server"

  return "unknown"
}

const failureFields = (e, reason = failureReason(e)) => {
  const body = e?.response?.data
  const detail = body && typeof body === "object" ? body.message || body.detail || body.error : null

  return {
    status: "error",
    progress: null,
    reason,
    message: typeof detail === "string" ? detail : "",
  }
}

export const addToUploadQueue = async ({ commit, dispatch, getters }, mediaList) => {
  commit(
    "addToUploadQueue",
    mediaList.map((media) => ({ ...media, id: `${Date.now()}-${++uploadSequence}` }))
  )

  if (!getters.isUploadInFlight) {
    dispatch("uploadNext")
  }
}

export const uploadNext = async ({ dispatch, getters }) => {
  if (getters.pendingUploads.length > 0) {
    dispatch("uploadMedia", { ...getters.pendingUploads[0] })
  }
}

export const uploadMedia = async ({ commit, dispatch }, media) => {
  const controller = new AbortController()
  commit("setUploadAbortController", controller)

  commit("updateQueuedMedia", {
    media,
    fields: { status: "uploading", progress: 0 },
  })

  try {
    const config = {
      signal: controller.signal,
      onUploadProgress: function (progressEvent) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)

        commit("updateQueuedMedia", {
          media,
          fields: { progress: percentCompleted },
        })
      },
    }

    const response = await api.uploadMediaFile(media.stationKey, media.file, config)

    commit("updateQueuedMedia", {
      media,
      fields: { status: "processing", task: response.task_id, progress: 0 },
    })

    media.task = response.task_id

    setTimeout(() => {
      dispatch("checkMediaUploadStatus", media)
    }, 1000)
  } catch (e) {
    if (e?.message === "canceled") {
      commit("updateQueuedMedia", {
        media,
        fields: { status: "canceled", progress: null },
      })
    } else {
      console.log(e)

      commit("updateQueuedMedia", { media, fields: failureFields(e) })
    }

    dispatch("scheduleUploadPrune")
  } finally {
    dispatch("uploadNext")
  }
}

export const checkMediaUploadStatus = async ({ commit, dispatch }, media) => {
  let response

  try {
    response = await api.getMediaUploadStatus(media.stationKey, media.task)
  } catch (e) {
    console.log(e)

    commit("updateQueuedMedia", { media, fields: failureFields(e) })

    dispatch("scheduleUploadPrune")

    return
  }

  switch (response.status) {
    case "SUCCESS": {
      commit("updateQueuedMedia", {
        media,
        fields: { progress: null, data: response.data },
      })

      dispatch("addUploadToPlaylist", { ...media, data: response.data })

      break
    }
    case "PROCESSING": {
      commit("updateQueuedMedia", {
        media,
        fields: {
          status: "processing",
          progress: response.progress,
        },
      })

      setTimeout(() => {
        dispatch("checkMediaUploadStatus", media)
      }, 1000)

      break
    }
    case "ERROR": {
      commit("updateQueuedMedia", {
        media,
        fields: {
          status: "error",
          progress: null,
          reason: "processing",
          message: typeof response.message === "string" ? response.message : "",
        },
      })

      dispatch("scheduleUploadPrune")

      break
    }
  }
}

export const addUploadToPlaylist = async ({ commit, dispatch, rootState }, media) => {
  try {
    const result = await addPlaylistTrack(media.stationKey, media.data.key)

    if (rootState.Autodj.stationKey === media.stationKey) {
      dispatch("Autodj/addPlaylistReferences", [result.reference], { root: true })
      dispatch("Autodj/refreshStorageUsage", media.stationKey, { root: true })
    }

    commit("updateQueuedMedia", { media, fields: { status: "success" } })
  } catch (e) {
    console.log(e)

    commit("updateQueuedMedia", { media, fields: failureFields(e, "playlist") })
  }

  dispatch("scheduleUploadPrune")
}

export const retryMediaUpload = async ({ commit, dispatch, getters }, media) => {
  if (media.reason === "playlist" && media.data?.key) {
    commit("updateQueuedMedia", {
      media,
      fields: { status: "processing", reason: null, message: "" },
    })

    dispatch("addUploadToPlaylist", media)

    return
  }

  commit("updateQueuedMedia", {
    media,
    fields: { status: "pending", reason: null, message: "", progress: null, task: "", data: {} },
  })

  if (!getters.isUploadInFlight) {
    dispatch("uploadNext")
  }
}

export const abortController = async ({ state, commit }) => {
  if (state.uploadAbortController) {
    state.uploadAbortController.abort()
    commit("setUploadAbortController", null)
  }
}

export const cancelUpload = async ({ commit, dispatch }) => {
  dispatch("abortController")
  commit("setUploadQueue", [])
}

export const cancelMediaUpload = async ({ commit, dispatch }, media) => {
  if (media.status === "uploading") {
    dispatch("abortController")
  }

  commit("updateQueuedMedia", {
    media,
    fields: {
      status: "canceled",
      progress: null,
    },
  })

  dispatch("scheduleUploadPrune")
}

export const scheduleUploadPrune = ({ commit, getters }) => {
  clearTimeout(pruneTimer)

  pruneTimer = setTimeout(() => {
    if (!getters.hasUploadsInFlight) {
      commit("removeFinishedUploads")
    }
  }, PRUNE_DELAY)
}
